'use client';

import React, { useImperativeHandle, useRef, useState, useEffect, ComponentProps } from 'react';
import ReactDatepicker, { DatePickerProps as ReactDatePickerProps, ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { flip, shift, offset } from '@floating-ui/react';
import cn from 'classnames';
import { parseUserDateInput } from '@dealroadshow/uikit-date-time';

import PortalWrp from '@creditflowresearch/uilib/Layout/PortalWrp';
import { FormError } from '@creditflowresearch/uilib/Form/FormError';
import type { IProps as IFormErrorProps } from '@creditflowresearch/uilib/Form/FormError/FormError';
import { FormStateSpecification } from '@creditflowresearch/uilib/Form/formStateSpecification';
import { FormField } from '@creditflowresearch/uilib/Form/FormField';
import { InputLabel } from '@creditflowresearch/uilib/Input/Label';

import DatePickerInput from './DatePickerInput';
import CalendarHeader from './CalendarHeader';
import {
  CalendarView,
  DatePickerSize,
  DEFAULT_DATE_FORMAT,
  DEFAULT_PLACEHOLDER,
  OVERFLOW_PADDING,
  TODAY_DAY_CLASS,
} from './constants';
import styles from './datePicker.module.scss';

type PopperModifiers = ComponentProps<typeof ReactDatepicker>['popperModifiers'];

type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

export type IDatePickerProps = DistributiveOmit<ReactDatePickerProps, 'value' | 'onChange'> & {
  dataTest: string,
  inputDataTest?: string,
  usePortal?: boolean,
  isPreventOverflowEnabled?: boolean,
  isFlipEnabled?: boolean,
  popperModifiers?: PopperModifiers,

  size?: DatePickerSize | 'S' | 'M' | 'L',
  label?: React.ReactNode,
  caption?: React.ReactNode,
  meta?: Pick<IFormErrorProps, 'touched' | 'modifiedSinceLastSubmit' | 'error' | 'warning' | 'submitError'>,
  isErrorMessage?: boolean,
  value?: Date | null,
  onChange?: (date: Date | null) => void,
  isClearable?: boolean,
  isDisabled?: boolean,
  isReadOnly?: boolean,
  placeholder?: string,
  formFieldClassName?: string,
  inputClassName?: string,
  isNarrow?: boolean,
};

export interface IDatePickerRef {
  setOpen: (open: boolean) => void,
  focus: () => void,
}

const PortalWrapper = ({ children }: { children: React.ReactNode }) => (
  <PortalWrp>
    <div className={ styles.datePickerPortalEncapsulationGlobalStyles }>
      { children }
    </div>
  </PortalWrp>
);

const DatePicker = (props: IDatePickerProps & { ref?: React.Ref<IDatePickerRef> }) => {
  const {
    ref,
    customInput,
    value,
    onChange,
    size = 'M',
    label,
    caption,
    meta,
    isErrorMessage = true,
    placeholder = DEFAULT_PLACEHOLDER,
    dateFormat = DEFAULT_DATE_FORMAT,
    isClearable = true,
    isDisabled = false,
    isReadOnly = false,
    onChangeRaw,
    dataTest,
    inputDataTest,
    name,
    minDate,
    maxDate,
    isFlipEnabled = true,
    isPreventOverflowEnabled = true,
    usePortal = false,
    popperModifiers = [],
    className,
    inputClassName,
    formFieldClassName,
    isNarrow,
    ...otherProps
  } = props;

  const datePickerRef = useRef<ReactDatepicker>(null);
  const inputRowRef = useRef<HTMLDivElement>(null);
  const headerDateRef = useRef<Date | null>(null);
  const pendingHeaderDateRef = useRef<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setView] = useState<CalendarView>(CalendarView.Days);

  useImperativeHandle(ref, () => ({
    setOpen: (open: boolean) => datePickerRef.current?.setOpen(open),
    focus: () => datePickerRef.current?.setFocus(),
  }), []);

  const handleCalendarOpen = () => setIsOpen(true);
  const handleCalendarClose = () => {
    setIsOpen(false);
    setView(CalendarView.Days);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleGlobalMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      if (target.closest('.react-datepicker-wrapper')) return;
      if (target.closest('.react-datepicker-popper')) return;
      datePickerRef.current?.setOpen(false);
    };
    document.addEventListener('mousedown', handleGlobalMouseDown);
    return () => document.removeEventListener('mousedown', handleGlobalMouseDown);
  }, [isOpen]);

  const handleChange = (date: Date | null) => {
    if (currentView !== CalendarView.Days) {
      if (currentView === CalendarView.Years && date && headerDateRef.current) {
        let corrected = new Date(date);
        corrected.setMonth(headerDateRef.current.getMonth());
        if (minDate && corrected < minDate) corrected = new Date(minDate);
        if (maxDate && corrected > maxDate) corrected = new Date(maxDate);
        pendingHeaderDateRef.current = corrected;
        queueMicrotask(() => {
          datePickerRef.current?.setPreSelection(corrected);
          pendingHeaderDateRef.current = null;
          setView(CalendarView.Days);
        });
        return;
      }
      setView(CalendarView.Days);
      return;
    }
    onChange?.(date);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange?.(null);
  };

  const handleRawChange = (event: React.SyntheticEvent) => {
    if (event.nativeEvent instanceof MouseEvent) return;
    const target = event.target as HTMLInputElement | null;
    if (!target?.value) {
      onChange?.(null);
      return;
    }
    const parsed = parseUserDateInput(target.value);
    if (parsed) {
      onChange?.(parsed);
    }
  };

  const handleRowClick = (event: React.MouseEvent) => {
    if (isDisabled) return;
    if ((event.target as HTMLElement).closest('.react-datepicker-popper')) return;
    datePickerRef.current?.setOpen(true);
  };

  const handleMonthChange = () => setView(CalendarView.Days);

  const dayClassName = (date: Date) => (
    date.toDateString() === new Date().toDateString() ? TODAY_DAY_CLASS : null
  );

  const renderCustomHeader = (headerProps: ReactDatePickerCustomHeaderProps) => {
    const displayDate = pendingHeaderDateRef.current ?? headerProps.monthDate ?? headerProps.date;
    headerDateRef.current = displayDate;
    return (
      <CalendarHeader
        { ...headerProps }
        date={ displayDate }
        currentView={ currentView }
        setView={ setView }
      />
    );
  };

  const hasError = FormStateSpecification.error(meta);
  const showClearButton = isClearable && !!value && !isDisabled;

  const mergedPopperModifiers = [
    offset(-5),
    isPreventOverflowEnabled &&
      shift({ padding: OVERFLOW_PADDING, boundary: document.body }),
    isFlipEnabled ? flip() : {
      name: 'resetPlacement',
      fn: (state) => ({ reset: { placement: state.initialPlacement } }),
    },
    ...popperModifiers,
  ].filter(Boolean) as NonNullable<PopperModifiers>;

  return (
    <FormField
      className={ cn(styles.wrapper, styles.datePickerEncapsulationGlobalStyles, styles[`size${ size }`], formFieldClassName, className) }
      dataTest={ dataTest }
      isNarrow={ isNarrow }
      isValidationFeedback={ hasError }
    >
      { label && (
        <InputLabel htmlFor={ name } className={ styles.label }>
          { label }
        </InputLabel>
      ) }
      <ReactDatepicker
        customInput={ customInput ?? (
          <DatePickerInput
            rowRef={ inputRowRef }
            dataTest={ dataTest }
            inputClassName={ inputClassName }
            inputDataTest={ inputDataTest }
            hasError={ hasError }
            isDisabled={ isDisabled }
            isOpen={ isOpen }
            showClearButton={ showClearButton }
            onRowClick={ handleRowClick }
            onClear={ handleClear }
          />
        ) }
        selected={ value }
        onChange={ handleChange as never }
        onChangeRaw={ isReadOnly ? (e) => e.preventDefault() : (onChangeRaw ?? handleRawChange) }
        dateFormat={ dateFormat }
        placeholderText={ placeholder }
        disabled={ isDisabled }
        onKeyDown={ isReadOnly ? (e) => {
            const navigationKeys = ['Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
            if (!navigationKeys.includes(e.key)) {
              e.preventDefault();
            }
          } : undefined }
        minDate={ minDate }
        maxDate={ maxDate }
        id={ name }
        name={ name }
        popperPlacement="bottom-end"
        popperContainer={ usePortal ? PortalWrapper : React.Fragment }
        shouldCloseOnSelect={ currentView === CalendarView.Days }
        showMonthYearPicker={ currentView === CalendarView.Months }
        showFullMonthYearPicker
        showYearPicker={ currentView === CalendarView.Years }
        onMonthChange={ handleMonthChange }
        onCalendarOpen={ handleCalendarOpen }
        onCalendarClose={ handleCalendarClose }
        renderCustomHeader={ renderCustomHeader }
        dayClassName={ dayClassName }
        popperModifiers={ mergedPopperModifiers }
        { ...otherProps }
        ref={ datePickerRef }
      />
      { isErrorMessage && (
        <FormError { ...meta } className={ styles.errorMessage } />
      ) }
      { !hasError && caption && (
        <div className={ styles.caption } data-test={ `${ dataTest }Caption` }>
          { caption }
        </div>
      ) }
    </FormField>
  );
};

export default DatePicker;
